(() => {
  if (window.yisuntrekAccessoryFilters) return;
  window.yisuntrekAccessoryFilters = true;
  const states = new WeakMap();
  let pickerId = 0;

  function update(root) {
    const model = root.querySelector('[data-accessory-model]');
    if (!model) return;
    const active = root.querySelector('[data-accessory-filter][aria-pressed="true"]');
    const category = active?.dataset.accessoryFilter || 'all';
    let count = 0;
    root.querySelectorAll('[data-accessory-card]').forEach(card => {
      const models = (card.dataset.models || '').split(',');
      const categories = (card.dataset.categories || '').split(',');
      card.hidden = !((model.value === 'all' || models.includes(model.value)) && (category === 'all' || categories.includes(category)));
      if (!card.hidden) count += 1;
    });
    const grid = root.querySelector('.accessory-grid');
    if (grid) {
      grid.scrollLeft = 0;
      grid.hidden = count === 0;
    }
    const empty = root.querySelector('[data-accessory-empty]');
    if (empty) empty.hidden = count !== 0;
    const countLabel = root.querySelector('.accessory-count');
    if (countLabel) countLabel.textContent = `${count} ${count === 1 ? 'collection' : 'collections'} · ${model.selectedOptions[0]?.textContent || ''} · ${active?.textContent || ''}`;
  }

  function rowHeight(state) {
    return state.buttons[0]?.getBoundingClientRect().height || Number.parseFloat(getComputedStyle(state.wheel).getPropertyValue('--am-row')) || 56;
  }

  function selectIndex(state, index, filter = true) {
    index = Math.max(0, Math.min(state.options.length - 1, index));
    const changed = state.select.value !== state.options[index].value;
    state.index = index;
    state.select.value = state.options[index].value;
    state.buttons.forEach((button, optionIndex) => button.setAttribute('aria-selected', String(optionIndex === index)));
    state.wheel.setAttribute('aria-activedescendant', state.buttons[index].id);
    if (changed && filter) update(state.root);
  }

  function render(state) {
    state.frame = null;
    if (!state.root.isConnected) return;
    const position = Math.max(0, Math.min(state.options.length - 1, state.wheel.scrollTop / state.row));
    if (state.destination !== null) {
      if (Math.abs(state.wheel.scrollTop - state.destination * state.row) <= 1) state.destination = null;
      else return;
    }
    const index = Math.round(position);
    if (index !== state.index) selectIndex(state, index);
  }

  function schedule(state) {
    if (!state.frame) state.frame = requestAnimationFrame(() => render(state));
  }

  function scrollToIndex(state, index, animate = true) {
    state.destination = index;
    state.wheel.scrollTo({ top: index * state.row, behavior: animate && !state.motion.matches ? 'smooth' : 'instant' });
    schedule(state);
  }

  function finishDrag(state, event) {
    const drag = state.drag;
    if (!drag || (event && event.pointerId !== drag.id)) return;
    state.drag = null;
    if (state.wheel.hasPointerCapture?.(drag.id)) state.wheel.releasePointerCapture(drag.id);
    if (!drag.moved) return;
    state.suppressClickUntil = performance.now() + 400;
    state.wheel.classList.remove('is-dragging');
    const index = Math.max(0, Math.min(state.options.length - 1, Math.round(state.wheel.scrollTop / state.row)));
    selectIndex(state, index);
    scrollToIndex(state, index);
    state.wheel.focus({ preventScroll: true });
  }

  function destroy(root) {
    const state = states.get(root);
    if (!state) return;
    state.controller.abort();
    state.resizeObserver?.disconnect();
    cancelAnimationFrame(state.frame);
    if (state.drag && state.wheel.hasPointerCapture?.(state.drag.id)) state.wheel.releasePointerCapture(state.drag.id);
    state.wheel.classList.remove('is-dragging');
    state.select.hidden = state.selectWasHidden;
    state.picker.hidden = true;
    state.wheel.replaceChildren();
    state.wheel.removeAttribute('aria-activedescendant');
    states.delete(root);
  }

  function initPicker(root) {
    destroy(root);
    const select = root.querySelector('[data-accessory-model]');
    const picker = root.querySelector('[data-accessory-model-picker]');
    const wheel = root.querySelector('[data-accessory-model-wheel]');
    if (!select || !picker || !wheel) return;
    const options = Array.from(select.options).filter(option => !option.hidden && !option.disabled);
    if (!options.length) return;
    const controller = new AbortController();
    const signal = controller.signal;
    if (!wheel.id) wheel.id = `${select.id || `accessory-model-${++pickerId}`}-wheel`;
    const buttons = options.map((option, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'accessory-model-option';
      button.dataset.accessoryModelOption = String(index);
      button.value = option.value;
      button.id = `${wheel.id}-option-${index}`;
      button.setAttribute('role', 'option');
      button.tabIndex = -1;
      button.textContent = option.textContent;
      return button;
    });
    wheel.replaceChildren(...buttons);
    const state = {
      root, select, picker, wheel, options, buttons, controller,
      selectWasHidden: select.hidden,
      motion: window.matchMedia('(prefers-reduced-motion: reduce)'),
      row: 56, index: -1, destination: null, frame: null, drag: null,
      suppressClickUntil: 0, typeBuffer: '', typedAt: 0
    };
    states.set(root, state);
    picker.hidden = false;
    select.hidden = true;
    state.row = rowHeight(state);
    selectIndex(state, Math.max(0, options.findIndex(option => option.value === select.value)), false);
    scrollToIndex(state, state.index, false);
    wheel.addEventListener('scroll', () => schedule(state), { passive: true, signal });
    wheel.addEventListener('wheel', () => { state.destination = null; schedule(state); }, { passive: true, signal });
    wheel.addEventListener('click', event => {
      const button = event.target.closest('[data-accessory-model-option]');
      if (!button || !wheel.contains(button)) return;
      event.preventDefault();
      if (performance.now() < state.suppressClickUntil) return;
      const index = Number(button.dataset.accessoryModelOption);
      selectIndex(state, index);
      scrollToIndex(state, index);
      wheel.focus({ preventScroll: true });
    }, { signal });
    wheel.addEventListener('keydown', event => {
      let target;
      if (event.key === 'ArrowUp') target = Math.max(0, state.index - 1);
      else if (event.key === 'ArrowDown') target = Math.min(options.length - 1, state.index + 1);
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = options.length - 1;
      else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const now = performance.now();
        const key = event.key.toLocaleLowerCase();
        state.typeBuffer = now - state.typedAt < 700 ? state.typeBuffer + key : key;
        state.typedAt = now;
        let needle = state.typeBuffer;
        if (Array.from(needle).every(character => character === key)) needle = key;
        const start = needle.length === 1 ? state.index + 1 : state.index;
        for (let offset = 0; offset < options.length; offset += 1) {
          const index = (start + offset) % options.length;
          if (options[index].textContent.trim().toLocaleLowerCase().startsWith(needle)) {
            target = index;
            break;
          }
        }
      } else return;
      event.preventDefault();
      if (target === undefined) return;
      selectIndex(state, target);
      scrollToIndex(state, target);
    }, { signal });
    wheel.addEventListener('pointerdown', event => {
      state.destination = null;
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      state.drag = { id: event.pointerId, y: event.clientY, x: event.clientX, top: wheel.scrollTop, moved: false };
    }, { passive: true, signal });
    wheel.addEventListener('pointermove', event => {
      const drag = state.drag;
      if (!drag || event.pointerId !== drag.id) return;
      if (!(event.buttons & 1)) { finishDrag(state, event); return; }
      const delta = event.clientY - drag.y;
      if (!drag.moved) {
        if (Math.abs(delta) < 6 || Math.abs(delta) < Math.abs(event.clientX - drag.x)) return;
        drag.moved = true;
        wheel.classList.add('is-dragging');
        wheel.setPointerCapture?.(drag.id);
      }
      event.preventDefault();
      wheel.scrollTop = drag.top - delta;
      schedule(state);
    }, { signal });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => wheel.addEventListener(name, event => finishDrag(state, event), { signal }));
    const label = root.querySelector(`label[for="${select.id}"]`);
    label?.addEventListener('click', event => {
      event.preventDefault();
      wheel.focus({ preventScroll: true });
    }, { signal });
    const resize = () => {
      const nextRow = rowHeight(state);
      if (nextRow === state.row) return;
      finishDrag(state);
      state.row = nextRow;
      scrollToIndex(state, state.index, false);
    };
    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(resize);
      state.resizeObserver.observe(wheel);
    }
    window.addEventListener('resize', resize, { passive: true, signal });
    state.motion.addEventListener('change', () => scrollToIndex(state, state.index, false), { signal });
  }

  function rootsIn(scope) {
    const roots = Array.from(scope.querySelectorAll('[data-accessories]'));
    if (scope.matches?.('[data-accessories]')) roots.unshift(scope);
    return roots;
  }

  function init(scope = document) {
    rootsIn(scope).forEach(root => { initPicker(root); update(root); });
  }

  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => rootsIn(event.target).forEach(destroy));
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-accessory-model]')) return;
    const root = event.target.closest('[data-accessories]');
    if (!root) return;
    const state = states.get(root);
    if (state) {
      selectIndex(state, Math.max(0, state.options.findIndex(option => option.value === event.target.value)), false);
      scrollToIndex(state, state.index, false);
    }
    update(root);
  });
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-accessory-filter],[data-accessory-reset]');
    if (!button) return;
    const root = button.closest('[data-accessories]');
    if (!root) return;
    const state = states.get(root);
    if (button.hasAttribute('data-accessory-reset')) {
      root.querySelector('[data-accessory-model]').value = 'all';
      if (state) {
        finishDrag(state);
        selectIndex(state, Math.max(0, state.options.findIndex(option => option.value === 'all')), false);
        scrollToIndex(state, state.index, false);
      }
    }
    const target = button.dataset.accessoryFilter || 'all';
    root.querySelectorAll('[data-accessory-filter]').forEach(filter => filter.setAttribute('aria-pressed', String(filter.dataset.accessoryFilter === target)));
    update(root);
  });
})();
